import { inject } from '@angular/core';
import {
  DeepLinkParametersEnum,
  HostDataReaderService,
  QuoteToolStrategyService,
  TimezoneService,
  withProccessedClassTime,
} from '@lx/quote-tool/shared/services';
import { QuoteOnlineSchool, Timezone } from '@lx/quote-tool/shared/types';
import { SchoolControllerApiService } from '@lx/shared/api';
import { SchoolV4 } from '@lx/shared/data-access/school';
import { Course, PredefinedOnlineSelections } from '@lx/shared/data-access/types';
import { getLocale } from '@lx/shared/i18n';
import { LoggerService } from '@lx/shared/logger';
import { SessionStorageService } from '@lx/shared/storage';
import { getCompanyHeader } from '@lx/shared/tokens';
import { CodeAndName } from '@lx/shared/types';
import { convertTimeWIthOffsetToString, isEmptyObj, normalizeString, QuoteToolPreselectedModes } from '@lx/shared/utils';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { z, ZodSafeParseResult } from 'zod';
import { ClassTime, OnlineCourseDeepLinkParametersSchema, OnlineDeepLinkParameters } from '@lx/quote-tool/lite/online/types';
import { CourseDetails, Flows, getQuoteToolStorageKey, SchoolLocation } from '@lx/quote-tool/lite/shared/types';
import { OnlineCourseService } from './online-course.service';

/**
 * Abstract base class for Online Course Mode Strategy.
 * This class provides the core logic for handling online course deep links
 * and can be extended by specific applications (QT Lite, Online Quote Tool, etc.)
 *
 * Generic Parameters:
 * - TRouteId: The route identifier type (e.g., RouteIds enum)
 * - TCourseDetails: The course details type (defaults to CourseDetails, can be extended with additional properties)
 * - TState: The application state type (defaults to unknown for maximum flexibility)
 *
 * Applications must implement:
 * - getStepToNavigate(): Define which step to navigate to
 * - getResidenceDataService(): Provide the residence data service
 * - getSchoolsObservable(): Provide the schools data stream
 * - getCurrentStepId(): Provide the current step ID for the application
 *
 * Required:
 * - Provide QUOTE_TOOL_STORAGE_KEY token with the storage key value
 */
export abstract class BaseOnlineCourseModeStrategyService<
  TRouteId,
  TCourseDetails extends CourseDetails = CourseDetails,
  TState = unknown & { currentStepId: string },
> implements QuoteToolStrategyService<TRouteId>
{
  protected readonly hostDataReader = inject(HostDataReaderService);
  protected readonly logger = inject(LoggerService);
  protected readonly locale = getLocale();
  protected readonly companyHeader = getCompanyHeader();
  protected readonly schoolControllerService = inject(SchoolControllerApiService);
  protected readonly timezoneService = inject(TimezoneService);
  protected readonly sessionStorageService = inject(SessionStorageService);
  protected readonly storageKey = getQuoteToolStorageKey();
  protected readonly onlineCourseService = inject(OnlineCourseService);

  /**
   * Returns the quote tool mode for this strategy
   */
  getQuoteToolMode(): QuoteToolPreselectedModes {
    return QuoteToolPreselectedModes.onlineCourse;
  }

  /**
   * Returns the step to navigate to after loading data
   * Must be implemented by the application
   */
  abstract getStepToNavigate(): Observable<TRouteId> | TRouteId;

  /**
   * Returns the residence country code for the user
   * Must be implemented by the application
   */
  protected abstract getResidenceCountryCode(): string | undefined;

  /**
   * Returns the shared data store to get all schools.
   *
   * This method is optional. If not overridden, it returns an empty array.
   *
   * **When to override:**
   * - QT Lite: Override to provide full schools list (needed for school location architecture)
   * - Online QT: No need to override - school name will default to "Online Courses"
   *
   * @returns Observable of schools array (defaults to empty array)
   */
  protected getSchoolsObservable(): Observable<SchoolV4[]> {
    return of([]);
  }

  /**
   * Returns the current step ID for the application
   * Must be implemented by the application
   * @protected
   */
  protected abstract getCurrentStepId(): TRouteId;

  /**
   * Main method to load data from deep link parameters
   */
  loadData$(): Observable<boolean> {
    const deepLinkParams = this.getDeepLinkParameters();
    const parsedParameters = this.parseDeepLinkParameters(deepLinkParams);

    if (!parsedParameters.success) {
      this.logger.error('Invalid deep link parameters', z.prettifyError(parsedParameters.error));
      return of(false);
    }

    const { schoolCode, courseCode, duration, classStartTime, classEndTime, deeplinkType, startDate } = deepLinkParams;

    const onlineSchools$ = this.schoolControllerService.findOnlineSchools(this.locale, this.companyHeader, this.getResidenceCountryCode());

    const allSchools$ = this.getSchoolsObservable().pipe(take(1));

    return combineLatest([onlineSchools$, allSchools$]).pipe(
      map(([onlineSchools, allSchools]) => this.buildSchoolLocation(onlineSchools, allSchools, schoolCode)),
      switchMap((selectedSchoolLocation) => {
        if (!selectedSchoolLocation) {
          return of(false);
        }

        const { schoolCode } = selectedSchoolLocation;

        return combineLatest([
          this.onlineCourseService.getOnlineCourses(schoolCode),
          this.onlineCourseService.getPredefinedOnlineSelections(schoolCode),
          this.timezoneService.timezones$,
        ]).pipe(
          map(([courses, predefinedSelections, timezones]) =>
            this.buildOnlineCourse(
              courses,
              predefinedSelections,
              timezones,
              selectedSchoolLocation,
              courseCode,
              duration,
              classStartTime,
              classEndTime,
              deeplinkType,
              startDate,
            ),
          ),
          tap((state: Partial<TState>) => {
            if (!isEmptyObj(state)) {
              this.saveState(state);
            }
          }),
          map((state: Partial<TState>) => !isEmptyObj(state)),
        );
      }),
      catchError((error) => {
        this.logger.error('Error during loadData$', error);
        return of(false);
      }),
    );
  }

  /**
   * Extracts deep link parameters from URL query params
   */
  protected getDeepLinkParameters(): OnlineDeepLinkParameters {
    const queryParams = this.hostDataReader.getQueryParams();
    const quoteToolModeQueryParam = this.hostDataReader.getQuoteToolMode();

    return {
      preselectedMode: quoteToolModeQueryParam ? (quoteToolModeQueryParam as QuoteToolPreselectedModes) : undefined,
      schoolCode: queryParams.get(DeepLinkParametersEnum.schoolCode) || undefined,
      courseCode: queryParams.get(DeepLinkParametersEnum.courseCode) || undefined,
      duration: Number(queryParams.get(DeepLinkParametersEnum.duration)) || undefined,
      classStartTime: queryParams.get(DeepLinkParametersEnum.classStartTime) || undefined,
      startDate: queryParams.get(DeepLinkParametersEnum.startDate) || undefined,
      classEndTime: queryParams.get(DeepLinkParametersEnum.classEndTime) || undefined,
      deeplinkType: queryParams.get(DeepLinkParametersEnum.deeplinkType) || undefined,
    };
  }

  /**
   * Validates deep link parameters using Zod schema
   */
  protected parseDeepLinkParameters(deepLinkParameters: OnlineDeepLinkParameters): ZodSafeParseResult<Partial<OnlineDeepLinkParameters>> {
    return OnlineCourseDeepLinkParametersSchema.safeParse(deepLinkParameters);
  }

  /**
   * Builds school location from online schools and provided code.
   *
   * If no matching school is found in allSchools (e.g., Online QT doesn't provide schools list),
   * the schoolName defaults to "Online Courses".
   *
   * @param onlineSchools - Online schools data from API
   * @param allSchools - Full schools list (may be empty for Online QT)
   * @param providedCode - Optional school code from deep link
   * @returns SchoolLocation object or null if no valid school code found
   */
  protected buildSchoolLocation(onlineSchools: QuoteOnlineSchool, allSchools: SchoolV4[], providedCode?: string): SchoolLocation | null {
    const schoolCodeToUse = onlineSchools?.onlineSchoolCodes?.length > 1 ? providedCode : onlineSchools?.onlineSchoolCodes?.[0];

    if (!schoolCodeToUse) {
      this.logger.error('No valid school code found.');
      return null;
    }

    const matchedSchool = allSchools.find((s) => normalizeString(s.code) === normalizeString(schoolCodeToUse));

    return {
      schoolCode: schoolCodeToUse,
      countryCode: 'ONL',
      countryName: $localize`Online`,
      schoolName: matchedSchool?.name || $localize`Online Courses`,
    };
  }

  /**
   * Builds course details from a Course object.
   * This method extracts relevant fields from the Course object and constructs a TCourseDetails object.
   * Applications can override this method to include additional fields in the course details.
   * @param course - The Course object from which to build course details
   * @returns TCourseDetails object containing course information
   * @protected
   */
  protected buildCourseDetails(course: Course): TCourseDetails {
    const { code, name, type, durationMin, durationMax, courseDates = [] } = course;

    return {
      code,
      name,
      type: type?.code,
      durationMin,
      durationMax,
      courseDates,
    } as TCourseDetails;
  }

  /**
   * Builds the online course data from retrieved information.
   * Applications can override this to construct extended course details types.
   */
  protected buildOnlineCourse(
    courses: Course[],
    predefinedSelections: PredefinedOnlineSelections[],
    timezones: CodeAndName[],
    selectedSchoolLocation: SchoolLocation,
    courseCode: string,
    courseDuration: number,
    classStartTime: string,
    classEndTime: string,
    deeplinkType: string,
    startDate?: string,
  ): Partial<TState> {
    const matchedCourse = courses.find((c) => normalizeString(c.code) === normalizeString(courseCode));
    if (!matchedCourse) {
      this.logger.error(`Course with code ${courseCode} not found.`);
      return {} as Partial<TState>;
    }

    const selectedCourse = this.buildCourseDetails(matchedCourse);

    const selectedTimezone = this.buildTimezone(timezones);
    const selectedClassTime = this.buildClassTime(selectedTimezone?.timezoneOffset, classStartTime, classEndTime);

    const {
      startDate: predefinedStartDate,
      endDate: predefinedEndDate,
      duration,
    } = predefinedSelections.find((ps) => normalizeString(ps.courseId) === normalizeString(courseCode)) || {};

    return this.buildStateObject({
      selectedSchoolLocation,
      selectedCourse,
      selectedTimezone,
      selectedClassTime,
      selectedDuration: courseDuration || duration,
      selectedStartDate: startDate || predefinedStartDate,
      selectedEndDate: !startDate ? predefinedEndDate : undefined,
      selectedLanguage: matchedCourse.courseLanguage,
      deeplinkType,
    });
  }

  /**
   * Builds timezone information from browser timezone
   */
  protected buildTimezone(timeZones: CodeAndName[]): Timezone {
    return this.timezoneService.buildTimezoneFromBrowser(timeZones);
  }

  /**
   * Builds class time information from start and end times
   */
  protected buildClassTime(timezoneOffset: number, start?: string, end?: string): ClassTime {
    const startTime = withProccessedClassTime({ loggerService: this.logger }, start) || '';
    const endTime = withProccessedClassTime({ loggerService: this.logger }, end) || '';
    const label = `${convertTimeWIthOffsetToString(startTime, timezoneOffset)} - ${convertTimeWIthOffsetToString(endTime, timezoneOffset)}`;
    return {
      startTime,
      endTime,
      label: startTime && endTime ? label : $localize`I will decide later`,
    };
  }

  /**
   * Builds the application-specific state object from the online course data.
   * This implementation is standard across all applications.
   * Applications can override this method if they need custom state structure.
   */
  protected buildStateObject(data: {
    selectedSchoolLocation: SchoolLocation;
    selectedCourse: TCourseDetails;
    selectedTimezone: Timezone;
    selectedClassTime: ClassTime;
    selectedDuration?: number;
    selectedStartDate?: string;
    selectedEndDate?: string;
    selectedLanguage?: string;
    deeplinkType?: string;
  }): Partial<TState & { currentStepId: TRouteId }> {
    const {
      selectedSchoolLocation,
      selectedCourse,
      selectedTimezone,
      selectedClassTime,
      selectedDuration,
      selectedStartDate,
      selectedEndDate,
      selectedLanguage,
      deeplinkType,
    } = data;
    return {
      activeFlow: Flows.Online,
      selectedLanguage,
      selectedSchoolLocation,
      selectedCourse,
      selectedClassTime,
      selectedTimezone,
      selectedDuration,
      selectedStartDate,
      selectedEndDate,
      deeplinkType,
      currentStepId: this.getCurrentStepId(),
    } as unknown as Partial<TState & { currentStepId: TRouteId }>;
  }

  /**
   * Saves the state object to session storage.
   * Uses the storage key from QUOTE_TOOL_STORAGE_KEY injection token.
   * Applications can override this method if they need different storage behavior.
   *
   * @param state - The state object to save
   */
  protected saveState(state: Partial<TState>): void {
    this.sessionStorageService.set(this.storageKey, state);
  }
}
